import React, { useCallback, useEffect, useState } from 'react';
import {
  AcademicCapIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  PencilIcon,
  PhoneIcon,
  PlusIcon,
  TrashIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { usePageTitle } from '../hooks/usePageTitle';
import { schoolsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { isStaff } from '../types/auth';
import type { SaveSchoolRequest, School, SchoolPlayer } from '../types/school';
import Modal from '../components/ui/Modal';
import SlideOver from '../components/ui/SlideOver';
import DeleteConfirmModal from '../components/ui/DeleteConfirmModal';
import EmptyState from '../components/ui/EmptyState';
import { getErrorMessage } from '../utils/errorMessage';

const emptyForm: SaveSchoolRequest = { name: '', address: '', contactName: '', email: '', phone: '', notes: '' };

const inputClass =
  'w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

/**
 * Iskolák: ahová a játékosok járnak. A kapcsolattartó e-mail címére mennek a
 * kikérők, felmentések és igazolások (a Dokumentumok oldalról, vagy az
 * esemény kikérőinél tömegesen).
 */
const Schools: React.FC = () => {
  usePageTitle('Iskolák');
  const { user } = useAuth();
  const { showToast } = useToast();
  const staff = isStaff(user);

  const [schools, setSchools] = useState<School[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<School | null>(null);
  const [form, setForm] = useState<SaveSchoolRequest>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<School | null>(null);
  const [detail, setDetail] = useState<School | null>(null);
  const [detailPlayers, setDetailPlayers] = useState<SchoolPlayer[] | null>(null);

  const load = useCallback(async () => {
    try {
      setSchools(await schoolsAPI.getAll());
    } catch (err) {
      console.error('Az iskolák betöltése nem sikerült:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (staff) load();
  }, [load, staff]);

  useEffect(() => {
    if (!detail) return;
    setDetailPlayers(null);
    schoolsAPI
      .getPlayers(detail.id)
      .then(setDetailPlayers)
      .catch((err) => {
        console.error('Az iskola játékosainak betöltése nem sikerült:', err);
        setDetailPlayers([]);
      });
  }, [detail]);

  const openForm = (school: School | null) => {
    setEditing(school);
    setForm(
      school
        ? {
            name: school.name,
            address: school.address ?? '',
            contactName: school.contactName ?? '',
            email: school.email ?? '',
            phone: school.phone ?? '',
            notes: school.notes ?? '',
          }
        : emptyForm
    );
    setFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const clean = (v?: string | null) => (v && v.trim() ? v.trim() : null);
      const data: SaveSchoolRequest = {
        name: form.name.trim(),
        address: clean(form.address),
        contactName: clean(form.contactName),
        email: clean(form.email),
        phone: clean(form.phone),
        notes: clean(form.notes),
      };
      const saved = editing ? await schoolsAPI.update(editing.id, data) : await schoolsAPI.create(data);
      showToast('Iskola elmentve.');
      setFormOpen(false);
      if (detail?.id === saved.id) setDetail(saved);
      load();
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      const firstError = errors ? (Object.values(errors)[0] as string[] | undefined)?.[0] : undefined;
      showToast(err.response?.data?.message || firstError || 'A mentés nem sikerült.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    try {
      await schoolsAPI.delete(deleting.id);
      showToast('Iskola törölve.');
      if (detail?.id === deleting.id) setDetail(null);
      setDeleting(null);
      load();
    } catch (err: any) {
      showToast(getErrorMessage(err, 'A törlés nem sikerült.'), 'error');
    }
  };

  if (!staff) {
    return (
      <div className="rounded-lg bg-white py-12 text-center shadow">
        <p className="text-gray-500">Az iskolák kezelése a stáb (edzők, adminok) számára érhető el.</p>
        <p className="mt-1 text-sm text-gray-500">A gyermeked iskoláját a Profil oldalon tudod megadni.</p>
      </div>
    );
  }

  const term = search.trim().toLowerCase();
  const visible = term
    ? schools.filter((s) =>
        [s.name, s.address, s.contactName, s.email].some((v) => v?.toLowerCase().includes(term))
      )
    : schools;

  return (
    <div>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Iskolák</h1>
          <p className="text-sm text-gray-500">
            Ahová a játékosok járnak - a kikérőket, igazolásokat közvetlenül ide lehet elküldeni.
          </p>
        </div>
        <button
          type="button"
          onClick={() => openForm(null)}
          className="inline-flex items-center justify-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
        >
          <PlusIcon className="mr-2 h-5 w-5" />
          Új iskola
        </button>
      </div>

      {schools.length > 0 && (
        <div className="relative mb-4">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Keresés név, cím, kapcsolattartó alapján..."
            aria-label="Keresés az iskolák között"
            className={`${inputClass} pl-9`}
          />
        </div>
      )}

      {loading ? (
        <div className="rounded-lg bg-white p-6 text-center text-sm text-gray-500 shadow-sm">Betöltés...</div>
      ) : schools.length === 0 ? (
        <div className="rounded-lg bg-white shadow-sm">
          <EmptyState
            icon={AcademicCapIcon}
            title="Még nincs iskola"
            description="Vedd fel az iskolákat a kapcsolattartó e-mail címével, hogy a kikérőket közvetlenül nekik lehessen elküldeni."
            actionLabel="Új iskola"
            onAction={() => openForm(null)}
          />
        </div>
      ) : visible.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-500">Nincs a keresésnek megfelelő iskola.</p>
      ) : (
        <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {visible.map((school) => (
            <li key={school.id} className="flex flex-col rounded-lg bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-2">
                <button
                  type="button"
                  onClick={() => setDetail(school)}
                  className="min-w-0 text-left font-semibold text-gray-900 hover:text-primary-700"
                >
                  {school.name}
                </button>
                <div className="flex shrink-0 gap-1">
                  <button
                    type="button"
                    onClick={() => openForm(school)}
                    aria-label={`${school.name} szerkesztése`}
                    className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-primary-600"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleting(school)}
                    aria-label={`${school.name} törlése`}
                    className="flex h-9 w-9 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 hover:text-red-600"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <dl className="mt-2 flex-1 space-y-1 text-sm text-gray-600">
                {school.address && (
                  <div className="flex items-start gap-2">
                    <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
                    <span>{school.address}</span>
                  </div>
                )}
                {school.contactName && (
                  <div className="flex items-center gap-2">
                    <UserIcon className="h-4 w-4 shrink-0 text-gray-400" />
                    <span className="truncate">{school.contactName}</span>
                  </div>
                )}
                {school.email ? (
                  <div className="flex items-center gap-2">
                    <EnvelopeIcon className="h-4 w-4 shrink-0 text-gray-400" />
                    <a href={`mailto:${school.email}`} className="truncate text-primary-700 hover:underline">
                      {school.email}
                    </a>
                  </div>
                ) : (
                  <p className="text-xs text-amber-700">Nincs e-mail cím - ide nem lehet dokumentumot küldeni.</p>
                )}
                {school.phone && (
                  <div className="flex items-center gap-2">
                    <PhoneIcon className="h-4 w-4 shrink-0 text-gray-400" />
                    <a href={`tel:${school.phone}`} className="hover:underline">
                      {school.phone}
                    </a>
                  </div>
                )}
              </dl>

              <button
                type="button"
                onClick={() => setDetail(school)}
                className="mt-3 self-start text-sm font-medium text-primary-700 hover:underline"
              >
                {school.playerCount} játékos jár ide
              </button>
            </li>
          ))}
        </ul>
      )}

      <SlideOver isOpen={!!detail} onClose={() => setDetail(null)} title={detail?.name ?? ''} maxWidthClass="sm:max-w-lg">
        {detail && (
          <div className="space-y-4">
            {detail.notes && <p className="whitespace-pre-line text-sm text-gray-700">{detail.notes}</p>}
            <div>
              <h3 className="mb-2 text-sm font-semibold text-gray-900">
                Játékosok{detailPlayers ? ` (${detailPlayers.length})` : ''}
              </h3>
              {detailPlayers === null ? (
                <p className="text-sm text-gray-500">Betöltés...</p>
              ) : detailPlayers.length === 0 ? (
                <p className="text-sm text-gray-500">
                  Nincs (látható) játékos, aki ide jár. A játékos iskoláját a Játékosok oldalon, vagy a szülő a
                  saját profilján adhatja meg.
                </p>
              ) : (
                <ul className="divide-y divide-gray-200 rounded-md border border-gray-200">
                  {detailPlayers.map((p) => (
                    <li key={p.playerId} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                      <span className="font-medium text-gray-900">{p.name}</span>
                      <span className="text-xs text-gray-500">{p.ageGroup}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </SlideOver>

      <Modal isOpen={formOpen} onClose={() => setFormOpen(false)} title={editing ? 'Iskola szerkesztése' : 'Új iskola'}>
        <form onSubmit={handleSubmit} className="space-y-4 pb-4">
          <div>
            <label htmlFor="school-name" className="mb-1 block text-sm font-medium text-gray-700">
              Név
            </label>
            <input
              id="school-name"
              required
              maxLength={200}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Pl. Vörösmarty Mihály Általános Iskola"
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="school-address" className="mb-1 block text-sm font-medium text-gray-700">
              Cím
            </label>
            <input
              id="school-address"
              maxLength={300}
              value={form.address ?? ''}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className={inputClass}
            />
          </div>
          <div>
            <label htmlFor="school-contact" className="mb-1 block text-sm font-medium text-gray-700">
              Kapcsolattartó
            </label>
            <input
              id="school-contact"
              maxLength={100}
              value={form.contactName ?? ''}
              onChange={(e) => setForm({ ...form, contactName: e.target.value })}
              placeholder="Pl. igazgatóhelyettes, titkárság"
              className={inputClass}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="school-email" className="mb-1 block text-sm font-medium text-gray-700">
                E-mail
              </label>
              <input
                id="school-email"
                type="email"
                maxLength={256}
                value={form.email ?? ''}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputClass}
              />
              <p className="mt-1 text-xs text-gray-500">Ide mennek a kikérők, igazolások.</p>
            </div>
            <div>
              <label htmlFor="school-phone" className="mb-1 block text-sm font-medium text-gray-700">
                Telefon
              </label>
              <input
                id="school-phone"
                type="tel"
                maxLength={50}
                value={form.phone ?? ''}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>
          <div>
            <label htmlFor="school-notes" className="mb-1 block text-sm font-medium text-gray-700">
              Megjegyzés
            </label>
            <textarea
              id="school-notes"
              rows={2}
              maxLength={1000}
              value={form.notes ?? ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className={inputClass}
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={() => setFormOpen(false)}
              className="flex-1 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Mégse
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Mentés...' : 'Mentés'}
            </button>
          </div>
        </form>
      </Modal>

      <DeleteConfirmModal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title="Iskola törlése"
        message="Biztosan törlöd ezt az iskolát? Az ide járó játékosoknál az iskola üresre áll."
        itemName={deleting?.name ?? ''}
      />
    </div>
  );
};

export default Schools;
